import React, { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { RouteComponentProps } from 'react-router-dom';
import {
  ATMButton,
  ATMContainer,
  ATMGrid,
  ATMHeader,
  ATMLoader,
} from 'shared-it-appmod-ui';
import { useRequestContext } from 'src/contexts/request.context';
import { actionTypes } from 'src/ducks/request.duck';
import Lang from 'src/libraries/language';
import history from 'src/history';
import { AccessType, FlightCategory } from 'src/constants';
import {
  getCleanData,
  getRequestStatus,
  isRequestAccessible,
} from 'src/selectors/request.selector';
import Moment from 'src/libraries/moment.library';
import { IRequest } from 'src/models/request.model';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import Error404 from '../../error/error-404/error-404.component';
import FlightRequestForm from '../flight-request-form/flight-request-form.component';
import FlightRequestReview from '../flight-request-review/flight-request-review.component';

export type IProps = RouteComponentProps<{
  id: string;
  type: string;
}>;

const removeUploadId = (files) => {
  return files.map((file) => {
    const { uploadId, ...newFile } = file;
    return newFile;
  });
};

const FlightRequestDuplicate: React.FC<IProps> = ({ match }) => {
  const requestId = Number(match.params.id);
  const type = String(match.params.type);
  const { state, actions } = useRequestContext();

  const formRef = useRef<HTMLFormElement>(null);
  const [data, setData] = useState<IRequest | undefined>();
  const [review, setReview] = useState(false);
  const [isPast, setIsPast] = useState(false);

  useEffect(() => {
    actions.dataGET(requestId, type);
  }, [requestId, type, actions]);

  useEffect(() => {
    if (state.data) {
      setData({
        ...state.data,
        request: {
          ...state.data.request,
          flightDate: undefined,
          startTime: undefined,
          flightDuration: undefined,
          releaseTime: undefined,
        },
      } as IRequest);
    }
  }, [state.data, setData]);

  const fetchStatus = getRequestStatus(state, actionTypes.REQUEST_DATA_FETCH);

  const handleClick = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    throttle(async () => {
      if (data && data.request) {
        const formData = {
          ...data,
          request: {
            ...data.request,
            flightDate: Moment(data.request?.flightDate).format(
              'YYYY/MM/DD'
            ),
            flightEndDate: data.request?.flightEndDate
              ? Moment.tz(
                  Moment(data.request.flightEndDate).format('YYYY-MM-DD'),
                  'YYYY-MM-DD',
                  'America/Los_Angeles'
                ).utc().format('YYYY-MM-DDTHH:mm:ss')
              : null,
          },
        };
        let result;

        switch (formData.request.flightFlag) {
          case FlightCategory.PatrolPassenger: {
            if (formData.passengerFlight.circuitInfos) {
              const circuitInfos = formData.passengerFlight.circuitInfos.map(
                (val) => {
                  return {
                    circuitId: undefined,
                    flightId: val.flightId,
                    value: val.value,
                    requestId: undefined,
                  };
                }
              );
              formData.passengerFlight.circuitInfos = circuitInfos;
            }
            if (formData.passengerFlight.culturalFiles) {
              formData.passengerFlight.culturalFiles = removeUploadId(
                formData.passengerFlight.culturalFiles
              );
            }
            if (formData.passengerFlight.sensitiveFiles) {
              formData.passengerFlight.sensitiveFiles = removeUploadId(
                formData.passengerFlight.sensitiveFiles
              );
            }
            result = await actions.createPatrolPassengerPOST(formData);
            break;
          }

          case FlightCategory.ContractorPatrol: {
            if (formData.passengerContractorFlight.circuitInfos) {
              const circuitInfo =
                formData.passengerContractorFlight.circuitInfos.map((val) => {
                  return {
                    circuitId: undefined,
                    flightId: val.flightId,
                    value: val.value,
                    requestId: undefined,
                  };
                });
              formData.passengerContractorFlight.circuitInfos = circuitInfo;
            }
            result = await actions.createPatrolPassengerContractorPOST(
              formData
            );
            break;
          }

          case FlightCategory.ConstructionWork: {
            if (formData.constructionFlight.sensitiveFiles) {
              formData.constructionFlight.sensitiveFiles = removeUploadId(
                formData.constructionFlight.sensitiveFiles
              );
            }
            if (formData.constructionFlight.culturalFiles) {
              formData.constructionFlight.culturalFiles = removeUploadId(
                formData.constructionFlight.culturalFiles
              );
            }
            if (formData.constructionFlight.structureFiles) {
              formData.constructionFlight.structureFiles = removeUploadId(
                formData.constructionFlight.structureFiles
              );
            }
            result = await actions.createConstructionPOST(formData);
            break;
          }

          case FlightCategory.ContractorConstruction: {
            if (formData.constructionContractorFlight.sensitiveFiles) {
              formData.constructionContractorFlight.sensitiveFiles =
                removeUploadId(
                  formData.constructionContractorFlight.sensitiveFiles
                );
            }
            if (formData.constructionContractorFlight.structureFiles) {
              formData.constructionContractorFlight.structureFiles =
                removeUploadId(
                  formData.constructionContractorFlight.structureFiles
                );
            }
            if (formData.constructionContractorFlight.culturalFiles) {
              formData.constructionContractorFlight.culturalFiles =
                removeUploadId(
                  formData.constructionContractorFlight.culturalFiles
                );
            }
            if (formData.constructionContractorFlight.liftFiles) {
              formData.constructionContractorFlight.liftFiles = removeUploadId(
                formData.constructionContractorFlight.liftFiles
              );
            }
            if (formData.constructionContractorFlight.hecFiles) {
              formData.constructionContractorFlight.hecFiles = removeUploadId(
                formData.constructionContractorFlight.hecFiles
              );
            }
            if (formData.constructionContractorFlight.congestedFiles) {
              formData.constructionContractorFlight.congestedFiles =
                removeUploadId(
                  formData.constructionContractorFlight.congestedFiles
                );
            }
            result = await actions.createConstructionContractorPOST(formData);
            break;
          }

          case FlightCategory.DroneFlightInternal: {
            if (formData.droneFlightSDGE.sensitiveFiles) {
              formData.droneFlightSDGE.sensitiveFiles = removeUploadId(
                formData.droneFlightSDGE.sensitiveFiles
              );
            }
            result = await actions.createDroneInternalPOST(formData);
            break;
          }

          case FlightCategory.DroneFlightExternal: {
            if (formData.droneFlightContractor.sensitiveFiles) {
              formData.droneFlightContractor.sensitiveFiles = removeUploadId(
                formData.droneFlightContractor.sensitiveFiles
              );
            }
            if (formData.droneFlightContractor.culturalFiles) {
              formData.droneFlightContractor.culturalFiles = removeUploadId(
                formData.droneFlightContractor.culturalFiles
              );
            }
            if (formData.droneFlightContractor.landFiles) {
              formData.droneFlightContractor.landFiles = removeUploadId(
                formData.droneFlightContractor.landFiles
              );
            }
            if (formData.droneFlightContractor.safetyFiles) {
              formData.droneFlightContractor.safetyFiles = removeUploadId(
                formData.droneFlightContractor.safetyFiles
              );
            }
            result = await actions.createDroneExternalPOST(formData);
            break;
          }
        }

        if (result && result.payload) {
          if (result.payload) {
            ToastSuccess(Lang.MSG_REQUEST_CREATE_SUCCESS);
          }

          history.push('/flight');
        }
      }
    }, 1000),
    [data]
  );

  const handlePreview = useCallback(
    (formData) => {
      setData(getCleanData(formData));
      setReview(true);
    },
    [setReview, setData]
  );

  const handleDismiss = useCallback(() => {
    setIsPast(false);
  }, [setIsPast]);

  if (!state.data && fetchStatus.fetching) {
    return <ATMLoader active />;
  }

  if (
    !state.data ||
    (state.data &&
      !isRequestAccessible(
        {
          requestId,
          requestStatus: state.data.request.requestStatus,
          requestorNo: state.data.request.requestorNo,
        },
        AccessType.FLIGHT_REQUEST_DUPLICATE
      ))
  ) {
    return <Error404 />;
  }

  const status = [
    actionTypes.REQUEST_CREATE_PATROL_PASSENGER_DATA,
    actionTypes.REQUEST_CREATE_PATROL_PASSENGER_CONTRACTOR_DATA,
    actionTypes.REQUEST_CREATE_CONSTRUCTION_DATA,
    actionTypes.REQUEST_CREATE_CONSTRUCTION_CONTRACTOR_DATA,
    actionTypes.REQUEST_CREATE_DRONE_INTERNAL_DATA,
    actionTypes.REQUEST_CREATE_DRONE_EXTERNAL_DATA,
  ].some((actionType) => getRequestStatus(state, actionType).fetching);

  const buttons = review ? (
    <>
      <ATMButton
        secondary
        icon="arrow left"
        content={Lang.LBL_EDIT}
        onClick={() => setReview(false)}
      />
      <ATMButton
        primary
        content={Lang.LBL_SUBMIT}
        onClick={handleSubmit}
        loading={status}
        disabled={status}
      />
    </>
  ) : (
    <>
      <ATMButton
        secondary
        content={Lang.LBL_CANCEL}
        onClick={() => {
          history.goBack();
        }}
      />
      <ATMButton
        primary
        content={Lang.LBL_REVIEW_SUBMIT}
        onClick={handleClick}
      />
    </>
  );

  return (
    <ATMContainer>
      <ATMGrid>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column verticalAlign="middle">
            <ATMHeader>{Lang.TTL_FLIGHT_REQUEST_DUPLICATE}</ATMHeader>
          </ATMGrid.Column>
          <ATMGrid.Column textAlign="right" verticalAlign="top">
            {buttons}
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>

      {review ? (
        <FlightRequestReview data={data} />
      ) : (
        <FlightRequestForm
          key={`flight_form_${data?.request?.requestId ?? 'empty'}`}
          formRef={formRef}
          handleSubmit={handlePreview}
          defaultValues={data}
          isPast={isPast}
          handlePast={(value) => setIsPast(value)}
          handleDismiss={handleDismiss}
        />
      )}

      <ATMGrid columns={1}>
        <ATMGrid.Column textAlign="center" verticalAlign="top">
          {buttons}
        </ATMGrid.Column>
      </ATMGrid>
    </ATMContainer>
  );
};

export default FlightRequestDuplicate;
